import {
  PromotionRequirement,
  PromotionReward,
  Promotion,
} from "@lemmonhq/types";
import { humanize } from "../utils";

export const getRequirementText = (
  requirement: PromotionRequirement,
): string => {
  switch (requirement.type) {
    case "coin":
      return `${requirement.amount} ${humanize(requirement.coin_type)} coins`;
    case "product_category":
      if (requirement.category_ids?.length) {
        return `Buy ${requirement.quantity} specific products`;
      }
      return `Buy ${requirement.quantity} items`;
    case "product":
      if (requirement.product_ids?.length) {
        return `Buy ${requirement.quantity} specific products`;
      }
      return `Buy ${requirement.quantity} items`;
    case "context":
      return `${humanize(requirement.key)} = ${requirement.value}`;
    case "membership":
      return "Membership required";
    default:
      return "";
  }
};

/**
 * Positional translator, matching `useLocalization`'s `t(key, params)`.
 *
 * Optional so the manager app keeps its existing English output unchanged; the
 * customer app passes one, because rendering English into a French page is a
 * defect the merchant never asked for.
 */
export type RewardTextTranslator = (
  key: string,
  params?: (string | number)[],
) => string;

export type RewardTextOptions = {
  /** Redemption-tier price. Omit for rewards that are not bought with coins. */
  coinCost?: number;
  /** Merchant-facing singular label, for example "stamp" or "point". */
  coinLabel?: string | null;
};

export const getRewardText = (
  reward: PromotionReward,
  t?: RewardTextTranslator,
  options: RewardTextOptions = {},
): string => {
  const withCoinCost = (text: string) => {
    const cost = options.coinCost;
    if (!cost || cost <= 0) return text;
    const configuredLabel = options.coinLabel?.trim();
    const label = configuredLabel
      ? cost === 1 || configuredLabel.toLocaleLowerCase().endsWith("s")
        ? configuredLabel
        : `${configuredLabel}s`
      : t
        ? t(
            cost === 1
              ? "promotions.reward_text.coin"
              : "promotions.reward_text.coins",
          )
        : cost === 1
          ? "coin"
          : "coins";
    return `${cost} ${label} = ${text}`;
  };

  let text: string;
  switch (reward.type) {
    case "product":
      if (reward.product_name) {
        text = t
          ? t("promotions.reward_text.free_product", [
              reward.quantity,
              reward.product_name,
            ])
          : `Get ${reward.quantity} ${reward.product_name} free`;
        break;
      }
      text = t
        ? t("promotions.reward_text.free_items", [reward.quantity])
        : `Get ${reward.quantity} free item${reward.quantity > 1 ? "s" : ""}`;
      break;
    case "product_category": {
      const names = (reward.categories ?? [])
        .map((category) => category.name?.trim())
        .filter(Boolean)
        .join(", ");
      if (t) {
        text = names
          ? t(
              reward.quantity === 1
                ? "promotions.reward_text.free_category_item"
                : "promotions.reward_text.free_category_items",
              [reward.quantity, names],
            )
          : t("promotions.reward_text.free_items", [reward.quantity]);
        break;
      }
      text = names
        ? `${reward.quantity} free item${reward.quantity > 1 ? "s" : ""} of your choice: ${names}`
        : `${reward.quantity} free item${reward.quantity > 1 ? "s" : ""}`;
      break;
    }
    case "product_discount": {
      const value =
        reward.mode === "percentage"
          ? `${reward.value}%`
          : `CHF ${reward.value}`;
      text = t
        ? t("promotions.reward_text.off_products", [value])
        : `${value} off products`;
      break;
    }
    case "order_discount": {
      const value =
        reward.mode === "percentage"
          ? `${reward.value}%`
          : `CHF ${reward.value}`;
      text = t
        ? t("promotions.reward_text.off_order", [value])
        : `${value} off order`;
      break;
    }
    case "special_price": {
      const productCount = Object.keys(reward.prices).length;
      text = t
        ? t(
            productCount === 1
              ? "promotions.reward_text.special_price_one"
              : "promotions.reward_text.special_price",
            [productCount],
          )
        : `Special price${productCount > 1 ? "s" : ""} on ${productCount} product${productCount > 1 ? "s" : ""}`;
      break;
    }
    default:
      text = t ? t("promotions.reward_text.unknown") : "Unknown reward";
  }

  return withCoinCost(text);
};

export const formatPromotionDescription = (promotion: Promotion): string => {
  const requirementTexts = promotion.requirements
    .map(getRequirementText)
    .filter(Boolean);
  const rewardText = getRewardText(promotion.reward);

  const requirements =
    requirementTexts.length > 0
      ? `Requirements: ${requirementTexts.join(", ")}`
      : "";

  return `${rewardText}${requirements ? `. ${requirements}` : ""}`;
};

export const getPromotionBadgeText = (promotions: Promotion[]): string => {
  if (promotions.length === 0) return "";
  if (promotions.length === 1) return promotions[0].name;
  return `${promotions.length} Offers`;
};
